import type { ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { useSite } from '@on3/ui-lib/index';
import { externalApi } from '@on3/ui-lib/utils/api';
import { Filter, makeList } from '@public/components/Db/Filters/Filter';
import { TeamAvatar } from '@public/components/Image/TeamAvatar';
import { PrimaryButton } from '@public/components/UI/Buttons/PrimaryButton';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { Text } from '@public/components/UI/Typography/Text';
import type { ITeamDraftRankingsProps } from '@public/pages/nfl/draft/[year]/draft-history/team-draft-totals';
import { type AxiosError } from 'axios';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import styles from './TeamDraftRankings.module.scss';

export const sportKeyDict = {
  nfl: 1,
  nba: 2,
} as const;

export const TeamDraftRankings = ({
  teamRankings,
  title,
  years,
}: ITeamDraftRankingsProps) => {
  const { defaultYears } = useSite();
  const { nflDraftYear, nbaDraftYear } = defaultYears;
  const { query, asPath, pathname } = useRouter();
  const { year, page, 'year-span': span } = query;
  const league = asPath.split('/')[1] as keyof typeof sportKeyDict;
  const sportKey = sportKeyDict[league];
  const leagueDraftYear = sportKey === 2 ? nbaDraftYear : nflDraftYear;
  const [initialTeamRankingsList] = useState(teamRankings?.list);
  const [isLoadMoreDisabled, setIsLoadMoreDisabled] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseFloat(`${page || 1}`));
  const [teamRankingsList, setTeamRankingsList] = useState(
    initialTeamRankingsList || [],
  );

  const availableYears = years?.filter(
    (yr) => yr <= leagueDraftYear && yr > 2011,
  );
  const basePath = asPath.split('?')[0];

  const handleLoadMore: React.MouseEventHandler = useCallback(async () => {
    setIsLoadMoreDisabled(true);

    const params = {
      sportKey,
      year,
      yearSpan: span,
      page: parseFloat(`${currentPage || 0}`) + 1,
    };

    try {
      const response = await externalApi.get(
        `/public/rdb/v1/draft-pick-organization-rank`,
        {
          params,
        },
      );

      setTeamRankingsList([...teamRankingsList, ...response.data.list]);
      setCurrentPage(currentPage + 1);
    } catch (e: unknown) {
      const { response, message } = e as AxiosError<ErrorData>;

      console.error(
        'TeamDraftRankings Error: ',
        response?.data?.message || message,
      );
    } finally {
      setIsLoadMoreDisabled(false);
    }
  }, [sportKey, year, span, currentPage, teamRankingsList]);

  const getHref = (spanNum: number) => {
    if (spanNum === 1) {
      return basePath;
    }

    return {
      pathname,
      query: { ...query, 'year-span': spanNum },
    };
  };

  return (
    <section>
      <header className={styles.pageHeader}>
        <Text className={styles.pageTitle} variant="h1">
          {title}
        </Text>
        <div className={styles.filterWrapper}>
          <div className={styles.spanLinkContainer}>
            {span && +span !== 1 && (
              <TextLink
                className={styles.spanLink}
                href={getHref(1)}
                underline="none"
                variant="button"
              >
                1 yr
              </TextLink>
            )}
            <TextLink
              className={clsx(styles.spanLink, {
                [styles.active]: span === '3',
              })}
              href={getHref(3)}
              underline="none"
              variant="button"
            >
              Last 3 Yrs
            </TextLink>

            <TextLink
              className={clsx(styles.spanLink, {
                [styles.active]: span === '5',
              })}
              href={getHref(5)}
              underline="none"
              variant="button"
            >
              Last 5 Yrs
            </TextLink>
          </div>

          <Filter
            list={makeList(availableYears)}
            name="year"
            selected={`${year}`}
          />
        </div>
      </header>

      <section>
        <ol className={styles.teamRankings}>
          <li>
            <ol className={styles.teamRankingsHeader}>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                Rank
              </Text>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                Team
              </Text>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                1st round
              </Text>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                2nd round
              </Text>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                3rd round
              </Text>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                4-7 rounds
              </Text>
              <Text
                className={clsx(styles.headerText)}
                component="li"
                variant="subtitle2"
              >
                total
              </Text>
            </ol>
          </li>
          {!!teamRankingsList.length && (
            <li>
              <ol className={styles.teamRankingsList}>
                {teamRankingsList.map((team) => (
                  <li
                    className={styles.teamRow}
                    key={`${team.organization?.key}-${year}-${span}`}
                  >
                    <dl className={styles.teamInfo}>
                      <div className={styles.rankAndName}>
                        <div className={styles.rank}>{team.rank}</div>

                        <a
                          className={styles.teamName}
                          href={
                            `/${league}/draft/${year}/draft-history/draft-by-year/?team-key=${team.organization?.key}` ||
                            ''
                          }
                        >
                          <TeamAvatar
                            height={40}
                            slug={team?.organization?.slug}
                            src={team?.organization?.assetUrl}
                            width={40}
                          />

                          {team?.organization?.fullName?.replace(
                            `${team.organization.mascot}`,
                            '',
                          )}
                        </a>
                      </div>

                      <div className={styles.picksContainer}>
                        <Text className={styles.roundLabel} variant="subtitle1">
                          <span>1st</span>
                        </Text>
                        <span className={styles.value}>{team.firstRound}</span>
                      </div>
                      <div className={styles.picksContainer}>
                        <Text className={styles.roundLabel} variant="subtitle1">
                          <span>2nd</span>
                        </Text>
                        <span className={styles.value}>{team.secondRound}</span>
                      </div>
                      <div className={styles.picksContainer}>
                        <Text className={styles.roundLabel} variant="subtitle1">
                          <span>3rd</span>
                        </Text>
                        <span className={styles.value}>{team.thirdRound}</span>
                      </div>
                      <div className={styles.picksContainer}>
                        <Text className={styles.roundLabel} variant="subtitle1">
                          <span>4-7 rounds</span>
                        </Text>
                        <span className={styles.value}>
                          {team.fourthThroughSeventhRound}
                        </span>
                      </div>
                      <div
                        className={clsx(
                          styles.picksContainer,
                          styles.totalContainer,
                        )}
                      >
                        <Text className={styles.roundLabel} variant="subtitle1">
                          <span>total</span>
                        </Text>
                        <span className={styles.value}>{team.total}</span>
                      </div>
                    </dl>
                  </li>
                ))}
              </ol>
            </li>
          )}
        </ol>
        <PrimaryButton
          color="primary"
          disabled={isLoadMoreDisabled}
          onClick={handleLoadMore}
          variant="text"
        >
          Load More
        </PrimaryButton>
        {!teamRankingsList.length && (
          <Text>There is no draft data available for this selection.</Text>
        )}
      </section>
    </section>
  );
};
